import React, { useEffect, useRef, useState } from "react";
import DraggableLogo from "./DraggableLogo";

// Client-side CSS approximation of the exported document, driven by the template
// config. This mirrors the Figma preview pane; the final PDF/DOCX is produced by
// the server renderer, so this is a faithful preview, not the exact output.

const alignFor = (layout) =>
  layout === "left" ? "items-start text-left" : layout === "right" ? "items-end text-right" : "items-center text-center";

// Click-to-edit inline text. Shows `placeholder` (muted) when the value is empty;
// commits the typed text on blur / Enter.
const EditableText = ({ value, placeholder, onCommit, className = "", style, multiline = false }) => {
  const ref = useRef(null);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (editing && ref.current) {
      ref.current.focus();
      const r = document.createRange();
      r.selectNodeContents(ref.current);
      const s = window.getSelection();
      s.removeAllRanges();
      s.addRange(r);
    }
  }, [editing]);
  const commit = () => {
    setEditing(false);
    onCommit((ref.current?.innerText || "").trim());
  };
  if (editing) {
    return (
      <span
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onMouseDown={(e) => e.stopPropagation()}
        onBlur={commit}
        onKeyDown={(e) => {
          // Single-line fields commit on Enter; multi-line fields let Enter
          // insert a line break (commit happens on blur / click-away).
          if (e.key === "Enter" && !multiline) { e.preventDefault(); ref.current.blur(); }
          if (e.key === "Escape") { e.preventDefault(); setEditing(false); }
        }}
        className={`${className} outline-none ring-1 ring-[#BB42AE] rounded px-0.5`}
        style={{ whiteSpace: "pre-line", ...style }}
      >
        {value}
      </span>
    );
  }
  return (
    <span
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      title="Click to edit"
      className={`${className} cursor-text hover:bg-[#BB42AE]/10 rounded ${value ? "" : "opacity-60"}`}
      style={{ whiteSpace: "pre-line", ...style }}
    >
      {value || placeholder}
    </span>
  );
};

const CoverPreview = ({ config, onChange, selectedBlock, onSelectBlock }) => {
  const { cover_page: cover, logo } = config;
  const colors = cover.colors;
  const bg = cover.background_image_url;
  const ref = useRef(null);

  // "Artwork only" — the uploaded image IS the cover, so nothing is drawn over
  // it. Matches both exporters, which skip the whole text layer.
  if (cover.artwork_only) {
    return (
      <div className="relative w-full h-full bg-white overflow-hidden">
        {bg ? (
          <img src={bg} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[11px] text-[#A1A1AA] px-6 text-center">
            Upload a background image — it will be the whole cover.
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={ref} className="relative w-full h-full bg-white overflow-hidden p-7">
      {bg && (
        <img src={bg} alt="" className="absolute inset-0 w-full h-full object-cover" />
      )}
      <div className="relative z-10 flex flex-col h-full">
        {/* The logo is now a free-floating draggable overlay, so the column just
            centres the title and pins the fields to the bottom. */}
        <div className={`flex flex-col flex-1 justify-center ${alignFor(cover.content_layout)}`}>
          <EditableText
            value={cover.fields.title}
            placeholder="PROJECT PROPOSAL"
            onCommit={(t) => onChange?.("cover_page.fields.title", t)}
            className="text-[22px] font-bold leading-tight"
            style={{ color: colors.headings }}
          />
          <EditableText
            value={cover.fields.subtitle}
            placeholder="IT Services"
            multiline
            onCommit={(t) => onChange?.("cover_page.fields.subtitle", t)}
            className="text-[15px] mt-1"
            style={{ color: colors.body }}
          />
          <div className="w-24 h-[3px] mt-3" style={{ backgroundColor: colors.headings }} />
        </div>

        <CoverBlocks
          cover={cover}
          colors={colors}
          onChange={onChange}
          selected={selectedBlock}
          onSelect={onSelectBlock}
        />
      </div>

      <DraggableLogo
        logo={{ url: logo.url, ...logo.cover }}
        path="logo.cover"
        onChange={onChange || (() => {})}
        containerRef={ref}
      />
    </div>
  );
};

// The cover's free-form lines. Adding, reordering and deleting live in the config
// panel, which has room for real controls — here you get direct editing of the
// text itself, plus a selection ring shared with the panel row.
const CoverBlocks = ({ cover, colors, onChange, selected, onSelect }) => {
  const blocks = cover.blocks || [];
  const setAt = (i, key, v) =>
    onChange?.("cover_page.blocks", blocks.map((b, j) => (j === i ? { ...b, [key]: v } : b)));

  return (
    <div className={`flex flex-col gap-1.5 ${alignFor(cover.content_layout)}`}>
      {blocks.map((b, i) => (
        <div
          key={i}
          onMouseDown={() => onSelect?.(i)}
          className={`rounded-[2px] ${selected === i ? "ring-1 ring-[#BB42AE] ring-offset-1" : ""}`}
        >
          <EditableText
            value={b.label}
            placeholder="Label"
            onCommit={(t) => setAt(i, "label", t)}
            className="text-[10px] font-semibold"
            style={{ color: colors.headings }}
          />
          {b.label ? <span className="text-[10px] font-semibold" style={{ color: colors.headings }}>:</span> : null}
          <EditableText
            value={b.value}
            placeholder="Text"
            multiline
            onCommit={(t) => setAt(i, "value", t)}
            className="text-[11px] ml-1"
            style={{ color: colors.body }}
          />
        </div>
      ))}
    </div>
  );
};

// Header zone (colour band / accent line / none) with a horizontally-draggable
// logo sized to the band height.
const BodyHeader = ({ config, onChange }) => {
  const { body_pages: body, logo } = config;
  const colors = body.colors;
  const style = body.header_style;
  const ref = useRef(null);
  // No header band when style is "none" — and no logo either (matches the export).
  if (style === "none") return null;
  // ~0.62in of the 1/1.414 preview page — the fixed band height both exporters
  // use (a page with a band gets its own top margin, so the preset doesn't matter).
  return (
    <div
      ref={ref}
      className="relative mb-6"
      style={{
        height: 32,
        ...(style === "colour_band" ? { backgroundColor: colors.headings } : {}),
        ...(style === "accent_line" ? { borderBottom: `2px solid ${colors.headings}` } : {}),
      }}
    >
      <DraggableLogo
        logo={{ url: logo.url, ...logo.header }}
        path="logo.header"
        onChange={onChange || (() => {})}
        containerRef={ref}
        bandMode
      />
      {/* Optional header label, pinned opposite the (left-default) logo. Multi-
          line (Enter inserts a break); on a colour band it sits on the heading
          colour, so it's drawn white. */}
      <div className="absolute inset-y-0 right-0 flex items-center justify-end pl-2 max-w-[55%]">
        <EditableText
          value={body.header_text}
          placeholder="Add header text"
          multiline
          onCommit={(t) => onChange?.("body_pages.header_text", t)}
          className="text-[8px] text-right leading-tight"
          style={{ color: style === "colour_band" ? "#FFFFFF" : colors.headings }}
        />
      </div>
    </div>
  );
};

const BodyPreview = ({ config, onChange }) => {
  const { body_pages: body } = config;
  const colors = body.colors;
  const pn = body.page_number;
  const pad = body.page_margins === "small" ? "px-6 py-5" : "px-9 py-8";
  return (
    <div className={`relative w-full h-full bg-white overflow-hidden flex flex-col ${pad}`}>
      <BodyHeader config={config} onChange={onChange} />

      <div className="flex-1 overflow-hidden">
        <h2 className="text-[13px] font-bold" style={{ color: colors.headings }}>1. EXECUTIVE SUMMARY</h2>
        <p className="text-[10px] mt-1 leading-relaxed" style={{ color: colors.body }}>
          This proposal outlines a comprehensive approach to support Client Company Name in achieving its
          strategic objectives. Our team has carefully analyzed the current challenges, opportunities, and
          operational requirements to recommend a solution that delivers measurable value.
        </p>
        <h2 className="text-[13px] font-bold mt-3" style={{ color: colors.headings }}>2. PROJECT OVERVIEW / BACKGROUND</h2>
        <p className="text-[11px] font-semibold mt-1" style={{ color: colors.headings }}>Objectives</p>
        <p className="text-[10px] mt-0.5 leading-relaxed" style={{ color: colors.body }}>
          The primary objectives of this project include assessing current operational processes, designing
          solutions aligned to strategic goals, and establishing measurable performance indicators.
        </p>
      </div>

      {pn.show && (
        <div
          className="mt-2 pt-1.5 flex items-end justify-between text-[8px]"
          style={{ borderTop: `1.2px solid ${colors.headings}`, color: colors.body }}
        >
          <EditableText
            value={body.footer_text}
            placeholder="Proposal Title"
            multiline
            onCommit={(t) => onChange?.("body_pages.footer_text", t)}
            className="pr-2 leading-tight"
          />
          <span className="whitespace-nowrap">Page 1 of 12</span>
        </div>
      )}
    </div>
  );
};

const TemplatePreview = ({ tab, config, onChange, selectedBlock, onSelectBlock }) => (
  <div className="w-full max-w-[420px] aspect-[1/1.414] mx-auto shadow-md rounded-[4px] overflow-hidden border border-[#E4E4E7]">
    {tab === "body" ? (
      <BodyPreview config={config} onChange={onChange} />
    ) : (
      <CoverPreview
        config={config}
        onChange={onChange}
        selectedBlock={selectedBlock}
        onSelectBlock={onSelectBlock}
      />
    )}
  </div>
);

export default TemplatePreview;
